<script setup lang="ts">
import { SancareOcticon } from '@sancare/typescript-commons'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { SearchOperatorEnum } from '@/saved-searches/types'
import { ConditionGroup } from '@/store/modules/searches/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

import AdvancedSearchCriteriaGroup from './AdvancedSearchCriteriaGroup.vue'

const props = withDefaults(defineProps<{
  disabled: boolean
  mode: string
  pmsiType?: PmsiTypeEnum
}>(), {
  pmsiType: PmsiTypeEnum.MCO
})

const emit = defineEmits(['clear-message'])
const store = useStore()
const { t } = useI18n()

const globalCriteriaGroup = computed<ConditionGroup>(() => {
  const group = store.state.stayList?.currentSearch?.globalCriteriaGroup
  return group && Object.keys(group).length > 0 ? group : {}
})

const criteriaGroups = computed<ConditionGroup[]>(() => store.state.stayList.currentSearch.criteriaGroups)

const externalOperator = computed(() =>
  store.state.stayList.currentSearch.searchOperators === SearchOperatorEnum.AND_OR ? 'ET' : 'OU'
)

const internalOperator = computed(() =>
  store.state.stayList.currentSearch.searchOperators === SearchOperatorEnum.AND_OR ? 'OU' : 'ET'
)

onMounted(() => {
  if (Object.keys(globalCriteriaGroup.value).length === 0) {
    store.commit('initiateGlobalCriteriaGroup')
  }
})

const clearMessage = () => {
  emit('clear-message')
}

const addCriteriaGroup = () => {
  clearMessage()
  if (!props.disabled) {
    store.commit('addCriteriaGroup')
  }
}

const deleteCriteriaGroup = (id: number) => {
  clearMessage()
  store.commit('removeCriteriaGroup', id)
}
</script>

<template>
  <div class="advanced-search-criteria-groups">
    <div class="row justify-content-center mb-4">
      <div class="col-12 row align-items-stretch">
        <advanced-search-criteria-group
          :internal-operator="internalOperator"
          :group="globalCriteriaGroup"
          :group-is-global="true"
          :mode="mode"
          :pmsi-type="pmsiType"
          class="col-12 mb-4 mb-md-0"
        />
      </div>
    </div>

    <div class="px-5 px-md-3">
      <div class="criteria-groups-wrapper gap-3">
        <template
          v-for="(group, idx) in criteriaGroups"
          :key="group.id"
        >
          <advanced-search-criteria-group
            class="w-100"
            :internal-operator="internalOperator"
            :group="group"
            :group-is-global="false"
            :mode="mode"
            :pmsi-type="pmsiType"
            @delete-criteria-group="deleteCriteriaGroup"
            @clear-message="clearMessage"
          />
          <div
            class="operator d-flex align-items-center justify-content-center text-center"
          >
            {{ externalOperator }}
          </div>
        </template>


        <div class="d-flex flex-column flex-md-row align-items-center gap-2 align-items-stretch mb-2 pb-2">
          <div
            class="automation-criteria-group automation-criteria-group-add gap-2"
            :class="{ disabled }"
            @click="addCriteriaGroup"
          >
            <sancare-octicon
              name="diff-added"
              :width="30"
            />
            <div>{{ t('filter.advancedSearch.addCriteriaGroup') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.criteria-groups-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
}

.operator {
  width: 100%;
}

@media (min-width: 768px) {
  .criteria-groups-wrapper {
    grid-template-columns: 1fr 2rem 1fr;
  }

  .operator {
    width: 2rem;

    &:nth-child(4n) {
      display: none !important;
    }
  }
}

.automation-criteria-group-add {
  background: #f0f0f0;
  border-radius: .3rem;
  min-height: 10rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a0a0a0;
  cursor: pointer;
  border: solid 1px #e0e0e0;

  &:hover {
    border-color: #a0a0a0;
    color: #505050;
  }
  transition: border-color .15s, color .15s;

  &.disabled {
    cursor: default;

    &:hover {
      border-color: #e0e0e0;
      color: #a0a0a0;
    }
  }
}
</style>
